export const frontmatter = {
  title: "So why am I still coding at 1 AM?",
  description: "The workday is over, but the terminal is still open. Why I code for fun — and why it's not as crazy as it sounds.",
  date: "2024-04-28",
  tags: ["career", "development", "opinions"],
  readingTime: 6,
};

It's 1 AM. I just spent two hours wrestling a legacy Java service that has more XML config than actual logic. The PR is up, Slack is closed, my brain is a wrung-out sponge. The sane thing to do is shut the lid, eat something not-fried, watch football highlights, and let my neurons cool off.

But that's not what's happening. Work laptop's off. Personal one is on. Terminal's open. I'm elbow-deep in a Rust script to scrape financial data because an API's rate limits annoyed me earlier this week.

Why? It's the question. Are we just gluttons for punishment? Maybe a little. But the real answer is this isn't unpaid overtime — it's the antidote to my day job.

## My sandbox, my rules

Be honest about the 9-to-5: even in a great job, you're playing in someone else's sandbox. You're a highly-skilled professional working inside a maze of constraints — a decade of tech debt, architectural choices made by people who left years ago, a style guide that insists on `kebab-case` for some reason. You're a cog. A vital, well-paid cog. Still a cog.

My personal projects folder is the escape. It's my little universe where I'm the benevolent — and occasionally reckless — dictator.

It's also a vacation from technical debt. My day job is mostly about navigating and paying down debt. My side project starts with `git init` and a beautiful, empty repo. No legacy. No users yet. A blank canvas, which is so rare it borders on therapy.

<Callout emoji="💡">
  This is the purest form of problem-solving. No bureaucracy. No "let's align on the strategy" meetings. Just me, a problem I made up, and the dopamine hit when it finally compiles. A video game where I write the game and beat the high score.
</Callout>

Last month I spent a weekend over-engineering a smart-home dashboard with Docker, Grafana and a Raspberry Pi, just to display the temperature in my room. The stock app does it in one tap. But mine has a slick custom font and a real-time WebSocket feed. Ridiculous. Pointless. Deeply satisfying.

## The open-source rabbit hole

This solitary obsession is often the gateway drug to something bigger: the chaotic, wonderful world of open source. It rarely starts with grand ambition. It starts with a tiny annoyance. A typo in the docs. A misleading error message.

You think "someone should fix that." Then "wait, *I* can fix that."

I'll never forget my first PR. I was sure I'd get flamed for breaking some unwritten rule. I clicked Create and braced for "did you even read the contributing guide?"

What I got was: "Hey, thanks for catching this. Could you add a quick test?" That was it. Welcoming, constructive. When that PR merged, it was a bigger thrill than shipping a major feature at work. My tiny change was now part of a tool used by thousands.

It's not just code, either. Triage an issue, improve the docs, help someone in a discussion thread — those count too. Reputation in open source is built on the quality of your ideas and how willing you are to help.

It runs on completely different incentives than a day job:

| Factor | Day job | Open source |
| --- | --- | --- |
| Goal | Ship the feature, on time. | Fix the one thing that's annoying me. |
| Code | Follow patterns, be risk-averse. | Clean, tested, has to justify itself. |
| Feedback | Polite, structured, sometimes vague. | Direct, honest, almost always educational. |
| Reward | Paycheck and a "nice work" in Slack. | A "thanks" from a stranger and your name in `git log`. |

## My personal R&D department

Honest reason for a lot of this: career self-preservation. The framework that's hot today is legacy tomorrow. Your company isn't going to halt production for six months so the team can learn the new "Next.js killer."

So you've got two options: become a dinosaur, or run your own R&D on nights and weekends.

At work, introducing a new library means a proposal, three meetings, a security review, and a presentation. On my side project? `pnpm add cool-new-lib` and I'm testing it in 30 seconds. I figure out fast whether it's genius or garbage, on my own time.

This isn't just learning. It's hands-on practical research. It's how I form opinions based on experience instead of a Hacker News thread. My hedge against stagnation.

## Is it actually worth it?

Let's not romanticize this. The lifestyle is a double-edged sword and demands real sacrifice.

<ProsCons
  pros={[
    "You learn new tech way faster than at any job.",
    "You have total creative and technical control.",
    "You build a portfolio that proves you actually like this.",
    "It's genuinely fun if you like puzzles.",
    "You meet good people in open source.",
  ]}
  cons={[
    "Burnout risk is huge.",
    "It eats into time for other hobbies and life.",
    "The 'always on' brain isn't healthy.",
    "Your projects folder becomes a graveyard of good intentions.",
    "Explaining 'yak shaving' to a non-tech friend is impossible.",
  ]}
/>

That graveyard of half-finished projects isn't a failure. It's a library of lessons. Each one taught me something before the next shiny thing pulled my attention. That's a feature, not a bug.

So why am I still here at 1 AM?

It's not poetic. It's simpler. The day job is where I apply my skills. Side projects are where I forge them. It's how I stay curious, how I avoid getting cynical, and how I remind myself that I don't just write code for a paycheck — I genuinely love this stuff.

Okay, that was a solid bit of procrastination. This Rust compiler isn't going to argue with itself. Back to it.
